import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

interface BreadcrumbItem {
  label: string;
  path: string;
  active?: boolean;
}

interface AmbassadorNavState {
  ambassadorName?: string;
}

const SEGMENT_LABELS: Record<string, string> = {
  ambassador: 'Ambassadors',
  'data-report': 'Data Report',
  'qr-tracking': 'QR Tracking',
  surveys: 'Survey Tracking',
  'manage-users': 'Manage Users',
  'manage-site': 'Manage Site',
  logs: 'Logs',
};

function isUuidLike(segment: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);
}

export function useBreadcrumbs(): BreadcrumbItem[] {
  const location = useLocation();
  const navState = (location.state ?? {}) as AmbassadorNavState;

  return useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);

    if (segments.length === 0) {
      return [{ label: 'Home', path: '/', active: true }];
    }

    if (segments[0] === 'ambassador') {
      const items: BreadcrumbItem[] = [{ label: 'Ambassadors', path: '/ambassador', active: false }];

      if (segments.length === 1) {
        items[0].active = true;
        return items;
      }

      const idSegment = segments[1];
      if (idSegment === 'add') {
        items.push({ label: 'Add Ambassador', path: '/ambassador/add', active: true });
        return items;
      }

      if (isUuidLike(idSegment)) {
        const name = navState.ambassadorName?.trim() || 'Ambassador';
        items.push({ label: name, path: `/ambassador/${idSegment}`, active: false });

        if (segments[2] === 'edit') {
          items.push({
            label: 'Edit',
            path: `/ambassador/${idSegment}/edit`,
            active: true,
          });
        } else {
          items[items.length - 1].active = true;
        }
        return items;
      }
    }

    if (segments[0] === 'manage-site') {
      const items: BreadcrumbItem[] = [
        { label: 'Manage Site', path: '/manage-site', active: false },
      ];

      if (segments.length === 1) {
        items[0].active = true;
        return items;
      }

      if (segments[1] === 'add') {
        items.push({ label: 'Add Site', path: '/manage-site/add', active: true });
        return items;
      }

      if (isUuidLike(segments[1]) && segments[2] === 'edit') {
        items.push({
          label: 'Edit Site',
          path: `/manage-site/${segments[1]}/edit`,
          active: true,
        });
        return items;
      }
    }

    if (segments[0] === 'manage-users') {
      const items: BreadcrumbItem[] = [
        { label: 'Manage Users', path: '/manage-users', active: false },
      ];

      if (segments.length === 1) {
        items[0].active = true;
        return items;
      }

      if (segments[1] === 'add') {
        items.push({ label: 'Add User', path: '/manage-users/add', active: true });
        return items;
      }

      if (isUuidLike(segments[1]) && segments[2] === 'edit') {
        items.push({
          label: 'Edit User',
          path: `/manage-users/${segments[1]}/edit`,
          active: true,
        });
        return items;
      }
    }

    const items: BreadcrumbItem[] = [{ label: 'Home', path: '/', active: false }];

    let currentPath = '';
    for (const segment of segments) {
      currentPath += `/${segment}`;
      const label =
        SEGMENT_LABELS[segment] ??
        segment
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      items.push({ label, path: currentPath, active: false });
    }

    if (items.length > 0) {
      items[items.length - 1].active = true;
    }

    return items;
  }, [location.pathname, location.state, navState.ambassadorName]);
}
